import type { FnType } from '../../lib/util';
import type {
  ExposedFunctionResponse,
  PlanningAssistantCallbackResult,
  PlanningAssistantWorkerMessage,
  PlanningAssistantWorkerMessageCollection,
  PlanningAssistantWorkerResponse,
} from './PlanningAssistantWorkerTypes';
import { exposedWorkerFunctions } from './PlanningAssistantWorkerTypes';
import type { SerializableValue } from './ExtendedJSON';
import { jsonParse, jsonStringify } from './ExtendedJSON';
import type { ClientId } from './ClientId';

const isBrowser = typeof window !== 'undefined';

let workerInitializer: null | (() => Promise<Worker>) = null;

export function setPlanningAssistantWorkerInitializer(
  initializer: () => Promise<Worker>,
): void {
  workerInitializer = initializer;
}

const sendMessage = (
  worker: Worker,
  msg: PlanningAssistantWorkerMessage | PlanningAssistantCallbackResult,
  functionsMap: Map<ClientId, FnType<unknown[], unknown>>,
) => {
  /* Note: If there is a type error on the next line it's because the types in the parameters have changed and one of the used types is not listed as a SerializableType in ExtendedJSON.ts.
   *  The check is there to make sure we don't use any custom classes which cannot be deserialized on the worker.
   *  To get a better error, expand the params array to individual components and you should see the problematic param and type, such as:
   *  `worker.postMessage(jsonStringify([msg.params[0], msg.params[1], msg.params[2], msg.params[3], msg.params[4], msg.params[5], msg.params[6]]));`
   *  If the problem type is a custom class, implement .toJS and .fromJS for it and add its handling to the serializer (see DistanceMap and MultiKey for reference). Simple interface types can simply be added to the list of SerializableType.
   */
  worker.postMessage(jsonStringify(msg, { functionsMap }));
};

// Some hard to type code present, unfortunately resorting to 'as' casting. If you have an idea on how to improve this, please do so.
const remoteFunction =
  <
    FnName extends PlanningAssistantWorkerMessage['action'],
    Params extends PlanningAssistantWorkerMessageCollection[FnName]['params'],
  >(
    fnName: FnName,
  ) =>
  async (
    ...params: Params
  ): Promise<ExposedFunctionResponse<FnName>['result']> => {
    if (!isBrowser) {
      const fn = exposedWorkerFunctions[fnName] as FnType<
        Params,
        ExposedFunctionResponse<FnName>['result']
      >;
      return await Promise.resolve(fn(...params));
    }

    if (!workerInitializer) {
      throw new Error(`Cannot get worker: initializer not provided`);
    }
    /** New worker for every remote function call */
    const worker = await workerInitializer();
    /** Every function in our params mapped to a clientId. */
    const functionsMap = new Map<ClientId, FnType<unknown[], unknown>>();

    try {
      const result = await new Promise<
        (PlanningAssistantWorkerResponse & { type: 'result' })['result']
      >(async (resolve, reject) => {
        const handler: NonNullable<Worker['onmessage']> = async (
          e: MessageEvent<string>,
        ) => {
          let isFinished = true;
          try {
            const msg = jsonParse<PlanningAssistantWorkerResponse>(e.data);
            if (msg.type === 'error') {
              throw new Error(msg.message);
            }

            if (msg.type === 'result') {
              resolve(msg.result);
            } else if (msg.type === 'fn_callback') {
              isFinished = false;
              const fn = functionsMap.get(msg.fnId);
              if (!fn) {
                throw new Error(
                  `PlanningAssistantWorker: Function with id ${msg.fnId} not found`,
                );
              }
              const result = await fn(...msg.parameters);
              sendMessage(
                worker,
                {
                  type: 'fn_callback_result',
                  callId: msg.callId,
                  result: result as SerializableValue,
                },
                functionsMap,
              );
            }
          } catch (err) {
            console.error(
              `Failed to process PlanningAssistantWorker response message: ${err.message}`,
            );
            return reject(err);
          } finally {
            if (isFinished) {
              worker?.removeEventListener('message', handler);
            }
          }
        };

        worker.onmessage = handler;

        sendMessage(
          worker,
          {
            type: 'call',
            action: fnName,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params: params as any,
          },
          functionsMap,
        );
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return result as any;
    } finally {
      worker?.terminate();
    }
  };

// To implement more functions, add them here and in PlanningAssistantWorker.ts
export const getVoyageSuggestionsInWorker = remoteFunction(
  'getVoyageSuggestions',
);
export const getScenarioImprovementsInWorker = remoteFunction(
  'getScenarioImprovements',
);
